/**
 * billingStore.ts
 *
 * Zustand store for billing, subscription, and payment state.
 * Communicates with the billing REST API endpoints.
 * All prices are in ZAR cents (integer) — display as R with 2 decimal places.
 */
import { create } from 'zustand';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Subscription {
  id: string;
  sector: string;
  planTier: string;
  status: string;
  amountCents: number;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  nextPaymentDate?: string;
  cancelledAt?: string;
}

export interface BillingInvoice {
  id: string;
  amountCents: number;
  vatCents: number;
  totalCents: number;
  status: string;
  description?: string;
  paidAt?: string;
  periodStart?: string;
  periodEnd?: string;
  createdAt: string;
}

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'cancelled'
  | 'trial_expired'
  | 'grace_period';

export interface PaymentMethod {
  last4: string;
  cardType: string;
  expMonth: number;
  expYear: number;
  bank: string;
}

export interface EnterpriseInquiryPayload {
  companyName: string;
  email: string;
  sector: string;
  numberOfSites: number;
  numberOfAssets: number;
  hardwareNeeds: boolean;
  message?: string;
}

interface BillingState {
  subscription: Subscription | null;
  invoices: BillingInvoice[];
  trialEndsAt: string | null;
  subscriptionStatus: SubscriptionStatus;
  plan: string;
  paymentMethod: PaymentMethod | null;
  isLoading: boolean;
  error: string | null;

  fetchBillingStatus: () => Promise<void>;
  initCheckout: (
    sector: string,
    planTier: string,
  ) => Promise<{ authorizationUrl: string; reference: string; accessCode: string }>;
  verifyTransaction: (reference: string) => Promise<{ success: boolean }>;
  cancelSubscription: (reason?: string) => Promise<void>;
  fetchInvoices: () => Promise<void>;
  submitEnterpriseInquiry: (data: EnterpriseInquiryPayload) => Promise<void>;
  initCardUpdate: () => Promise<{ authorizationUrl: string; reference: string; accessCode: string }>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// ─── Store ───────────────────────────────────────────────────────────────────

export const useBillingStore = create<BillingState>((set, get) => ({
  subscription: null,
  invoices: [],
  trialEndsAt: null,
  subscriptionStatus: 'trialing',
  plan: 'free',
  paymentMethod: null,
  isLoading: false,
  error: null,

  fetchBillingStatus: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await fetch(`${API_BASE}/api/billing/status`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error('Failed to fetch billing status');
      const data = await res.json();
      // Parse payment method from response (Paystack authorization data)
      const pm = data.paymentMethod ?? data.authorization ?? null;
      set({
        subscription: data.subscription ?? null,
        trialEndsAt: data.trialEndsAt ?? null,
        subscriptionStatus: data.subscriptionStatus ?? 'trialing',
        plan: data.plan ?? 'free',
        invoices: data.invoices ?? [],
        paymentMethod: pm ? {
          last4: pm.last4 ?? pm.last_four ?? '',
          cardType: pm.cardType ?? pm.card_type ?? pm.brand ?? '',
          expMonth: pm.expMonth ?? pm.exp_month ?? 0,
          expYear: pm.expYear ?? pm.exp_year ?? 0,
          bank: pm.bank ?? '',
        } : null,
        isLoading: false,
      });
    } catch (err: any) {
      set({ isLoading: false, error: err.message });
    }
  },

  initCheckout: async (sector: string, planTier: string) => {
    const res = await fetch(`${API_BASE}/api/billing/checkout`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ sector, planTier }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message ?? 'Failed to initialize checkout');
    }
    return res.json();
  },

  verifyTransaction: async (reference: string) => {
    const res = await fetch(
      `${API_BASE}/api/billing/verify?reference=${encodeURIComponent(reference)}`,
      { headers: getHeaders() },
    );
    if (!res.ok) throw new Error('Verification failed');
    const data = await res.json();
    if (data.success) {
      await get().fetchBillingStatus();
    }
    return data;
  },

  cancelSubscription: async (reason?: string) => {
    const res = await fetch(`${API_BASE}/api/billing/cancel`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) throw new Error('Failed to cancel subscription');
    await get().fetchBillingStatus();
  },

  fetchInvoices: async () => {
    try {
      const res = await fetch(`${API_BASE}/api/billing/invoices`, {
        headers: getHeaders(),
      });
      if (!res.ok) { set({ invoices: [] }); return; }
      const data = await res.json();
      set({ invoices: Array.isArray(data) ? data : (data.invoices ?? []) });
    } catch {
      set({ invoices: [] });
    }
  },

  initCardUpdate: async () => {
    const res = await fetch(`${API_BASE}/api/billing/update-card`, {
      method: 'POST',
      headers: getHeaders(),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message ?? 'Failed to initialize card update');
    }
    return res.json();
  },

  submitEnterpriseInquiry: async (data: EnterpriseInquiryPayload) => {
    const res = await fetch(`${API_BASE}/api/billing/enterprise-inquiry`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message ?? 'Failed to submit inquiry');
    }
  },
}));
